import { ModelInstance, BaseModel, ToFrontHook, ToBackHook, DateTimeField, enumField, listField } from './util';
import { Team } from './team';
import { BasketballGameConfig } from './basketball-game-config';
import { VolleyballGameConfig } from './volleyball-game-config';
import { LeagueCourt } from './league-court';
import { MediaItem } from './media-item';
import { HockeyGameConfig } from './hockey-game-config';
import { Tournament } from './tournament';
import { FootballGameConfig } from './football-game-config';
import { HandballGameConfig } from './handball-game-config';
import { Playoff } from './playoff';
import { TournamentGroup } from './tournament-group';
import { File } from './file';
import { RugbyGameConfig } from './rugby-game-config';
import { WaterpoloGameConfig } from './waterpolo-game-config';
import { Sport } from './sport';
import { WrestballGameConfig } from './wrestball-game-config';

export enum GameStatuses {
  open = 1,
  in_progress,
  closed,
  archived,
  draft
}

export enum GameResultTypes {
  team_won = 1,
  competitor_team_won,
  team_technical_defeat,
  competitor_team_technical_defeat,
  draw
}

@ModelInstance({
  mappingFields: {
    mhl_carousel: 'mhlCarousel',
  }
})
export class GameAdditionalData {
  mhlCarousel: boolean;

  @ToFrontHook
  static toFront(data: any): any {}

  @ToBackHook
  static toBack(data: any): any {}
}

@ModelInstance({
  mappingFields: {
    nickname: 'nickname',
    color: 'color',
  }
})
export class GameTeamAdditionalData {
  nickname: string;
  color: string;

  @ToFrontHook
  static toFront(data: any): any {}

  @ToBackHook
  static toBack(data: any): any {}
}


@ModelInstance({
  mappingFields: {
    id: 'id',
    name: 'name',
    logo: 'logo',
    additional_data: 'additionalData',
  },
  relation: {
    logo: File,
    additionalData: GameTeamAdditionalData,
  }
})
export class TournamentTeamShort extends BaseModel {
  id: number;
  name: string;
  logo: File;
  additionalData: GameTeamAdditionalData;

  @ToFrontHook
  static toFront(data: any): any {}

  @ToBackHook
  static toBack(data: any): any {}
}

@ModelInstance({
  mappingFields: {
    team: 'team',
    tournament_team: 'tournamentTeam',
    score: 'score',
    score_by_period: 'scoreByPeriod',
    game_ids: 'gameIds',
  },
  relation: {
    team: Team,
    tournamentTeam: TournamentTeamShort,
  }
})
export class MhlCarouselTeam extends BaseModel {
  team: Team;
  tournamentTeam: TournamentTeamShort;
  score: number;
  scoreByPeriod: {[key: string]: number};
  gameIds: number[];

  @ToFrontHook
  static toFront(data: any): any {}

  @ToBackHook
  static toBack(data: any): any {}
}

@ModelInstance({
  mappingFields: {
    id: 'id',
    game_ids: 'gameIds',
    winner_team_id: 'winnerTeamId',
    teams: 'teams',
  },
  relation: {
    teams: listField(MhlCarouselTeam),
  }
})
export class MhlCarousel extends BaseModel {
  id: string;
  gameIds: number[];
  winnerTeamId: number;
  teams: MhlCarouselTeam[];

  @ToFrontHook
  static toFront(data: any): any {}

  @ToBackHook
  static toBack(data: any): any {}
}

@ModelInstance({
  mappingFields: {
    id: 'id',
    status: 'status',
    team: 'team',
    tournament_team: 'tournamentTeam',
    tournament_team_id: 'tournamentTeamId',
    team_id: 'teamId',
    competitor_team: 'competitorTeam',
    competitor_tournament_team: 'competitorTournamentTeam',
    competitor_tournament_team_id: 'competitorTournamentTeamId',
    competitor_team_id: 'competitorTeamId',
    competitor_team_name: 'competitorTeamName',
    datetime: 'datetime',
    location: 'location',
    team_score: 'teamScore',
    competitor_team_score: 'competitorTeamScore',
    result_type: 'resultType',
    playoff_number: 'playoffNumber',
    tournament_id: 'tournamentId',
    tournament_tour: 'tournamentTour',
    game_config: 'gameConfig',
    basketball_game_config: 'basketballGameConfig',
    volleyball_game_config: 'volleyballGameConfig',
    hockey_game_config: 'hockeyGameConfig',
    football_game_config: 'footballGameConfig',
    handball_game_config: 'handballGameConfig',
    rugby_game_config: 'rugbyGameConfig',
    waterpolo_game_config: 'waterpoloGameConfig',
    wrestball_game_config: 'wrestballGameConfig',
    score_by_period: 'scoreByPeriod',
    playoff_stage: 'playoffStage',
    playoff_round: 'playoffRound',
    tournament_stage_id: 'tournamentStageId',
    tournament_playoff_id: 'tournamentPlayoffId',
    tournament_playoff: 'tournamentPlayoff',
    tournament_court: 'tournamentCourt',
    media_count: 'mediaCount',
    media_live_count: 'mediaLiveCount',
    media: 'media',
    tournament: 'tournament',
    tournament_group: 'tournamentGroup',
    additional_data: 'additionalData',
    mhl_carousel: 'mhlCarousel',
  },
  relation: {
    status: enumField(GameStatuses),
    team: Team,
    tournamentTeam: TournamentTeamShort,
    competitorTeam: Team,
    competitorTournamentTeam: TournamentTeamShort,
    datetime: DateTimeField,
    resultType: enumField(GameResultTypes),
    basketballGameConfig: BasketballGameConfig,
    volleyballGameConfig: VolleyballGameConfig,
    hockeyGameConfig: HockeyGameConfig,
    footballGameConfig: FootballGameConfig,
    handballGameConfig: HandballGameConfig,
    rugbyGameConfig: RugbyGameConfig,
    waterpoloGameConfig: WaterpoloGameConfig,
    wrestballGameConfig: WrestballGameConfig,
    tournamentPlayoff: Playoff,
    tournamentCourt: LeagueCourt,
    media: listField(MediaItem),
    tournament: Tournament,
    tournamentGroup: TournamentGroup,
    additionalData: GameAdditionalData,
    mhlCarousel: MhlCarousel,
  }
})
export class Game extends BaseModel {
  private _basketballGameConfig: BasketballGameConfig;
  private _volleyballGameConfig: VolleyballGameConfig;
  private _hockeyGameConfig: HockeyGameConfig;
  private _footballGameConfig: FootballGameConfig;
  private _handballGameConfig: HandballGameConfig;
  private _rugbyGameConfig: RugbyGameConfig;
  private _waterpoloGameConfig: WaterpoloGameConfig;
  private _wrestballGameConfig: WrestballGameConfig;

  id: number;
  status: GameStatuses;
  team: Team;
  tournamentTeam: TournamentTeamShort;
  teamId: number;
  tournamentTeamId: number;
  competitorTeam: Team;
  competitorTournamentTeam: TournamentTeamShort;
  competitorTeamId: number;
  competitorTournamentTeamId: number;
  competitorTeamName: string;
  gameConfig: any;
  datetime: Date;
  location: string;
  teamScore: number;
  competitorTeamScore: number;
  resultType: GameResultTypes;
  playoffNumber: string;
  tournamentId: number;
  tournamentTour: number;
  scoreByPeriod: any;
  playoffStage: string;
  playoffRound: number;
  tournamentStageId: number;
  tournamentPlayoffId: number;
  tournamentPlayoff: Playoff;
  tournamentCourt: LeagueCourt;
  mediaCount: number;
  mediaLiveCount: number;
  media: MediaItem[];
  tournament: Tournament;
  tournamentGroup: TournamentGroup;
  additionalData: GameAdditionalData;
  mhlCarousel: MhlCarousel;

  get basketballGameConfig(): BasketballGameConfig {
    if (this._basketballGameConfig && this._basketballGameConfig.periodsCount) {
      return this._basketballGameConfig;
    }
    if (this.gameConfig) {
      this._basketballGameConfig = BasketballGameConfig.toFront(this.gameConfig);
    }
    return this._basketballGameConfig;
  }

  set basketballGameConfig(value) {
    this._basketballGameConfig = value;
  }

  get volleyballGameConfig(): VolleyballGameConfig {
    if (this._volleyballGameConfig && this._volleyballGameConfig.setsCount) {
      return this._volleyballGameConfig;
    }
    if (this.gameConfig) {
      this._volleyballGameConfig = VolleyballGameConfig.toFront(this.gameConfig);
    }
    return this._volleyballGameConfig;
  }

  set volleyballGameConfig(value) {
    this._volleyballGameConfig = value;
  }

  get hockeyGameConfig(): HockeyGameConfig {
    if (this._hockeyGameConfig && this._hockeyGameConfig.periodsCount) {
      return this._hockeyGameConfig;
    }
    if (this.gameConfig) {
      this._hockeyGameConfig = HockeyGameConfig.toFront(this.gameConfig);
    }
    return this._hockeyGameConfig;
  }

  set hockeyGameConfig(value) {
    this._hockeyGameConfig = value;
  }

  get handballGameConfig(): HandballGameConfig {
    if (this._handballGameConfig && this._handballGameConfig.periodsCount) {
      return this._handballGameConfig;
    }
    if (this.gameConfig) {
      this._handballGameConfig = HandballGameConfig.toFront(this.gameConfig);
    }
    return this._handballGameConfig;
  }

  set handballGameConfig(value) {
    this._handballGameConfig = value;
  }

  get footballGameConfig(): FootballGameConfig {
    if (this._footballGameConfig && this._footballGameConfig.periodsCount) {
      return this._footballGameConfig;
    }
    if (this.gameConfig) {
      this._footballGameConfig = FootballGameConfig.toFront(this.gameConfig);
    }
    return this._footballGameConfig;
  }

  set footballGameConfig(value) {
    this._footballGameConfig = value;
  }

  get rugbyGameConfig(): RugbyGameConfig {
    if (this._rugbyGameConfig && this._rugbyGameConfig.periodsCount) {
      return this._rugbyGameConfig;
    }
    if (this.gameConfig) {
      this._rugbyGameConfig = RugbyGameConfig.toFront(this.gameConfig);
    }
    return this._rugbyGameConfig;
  }

  set rugbyGameConfig(value) {
    this._rugbyGameConfig = value;
  }

  get waterpoloGameConfig(): WaterpoloGameConfig {
    if (this._waterpoloGameConfig && this._waterpoloGameConfig.periodsCount) {
      return this._waterpoloGameConfig;
    }
    if (this.gameConfig) {
      this._waterpoloGameConfig = WaterpoloGameConfig.toFront(this.gameConfig);
    }
    return this._waterpoloGameConfig;
  }

  set waterpoloGameConfig(value) {
    this._waterpoloGameConfig = value;
  }

  get wrestballGameConfig(): WrestballGameConfig {
    if (this._wrestballGameConfig && this._wrestballGameConfig.periodsCount) {
      return this._wrestballGameConfig;
    }
    if (this.gameConfig) {
      this._wrestballGameConfig = WrestballGameConfig.toFront(this.gameConfig);
    }
    return this._wrestballGameConfig;
  }

  set wrestballGameConfig(value) {
    this._wrestballGameConfig = value;
  }

  get scoreByPeriodList(): {period: number, teamScore: number, competitorTeamScore: number}[] {
    if (!this.scoreByPeriod) {
      return [];
    }
    return Object.keys(this.scoreByPeriod).map(period => ({
      period: +period,
      teamScore: this.scoreByPeriod[period][0],
      competitorTeamScore: this.scoreByPeriod[period][1]
    }));
  }

  get isClosed(): boolean {
    return this.status === GameStatuses.closed;
  }

  get isTeamWon() {
    return [GameResultTypes.team_won, GameResultTypes.competitor_team_technical_defeat].includes(this.resultType);
  }

  get isCompetitorTeamWon() {
    return [GameResultTypes.competitor_team_won, GameResultTypes.team_technical_defeat].includes(this.resultType);
  }

  get teamWinnerId() {
    if (!this.isClosed) {
      return null;
    }
    if (this.isTeamWon) {
      return this.team.id || this.teamId;
    }
    if (this.isCompetitorTeamWon) {
      return this.competitorTeam.id || this.competitorTeamId;
    }
    return null;
  }

  get teamLoserId() {
    if (!this.isClosed) {
      return null;
    }
    if (this.isTeamWon) {
      return this.competitorTeam.id || this.competitorTeamId;
    }
    if (this.isCompetitorTeamWon) {
      return this.team.id || this.teamId;
    }
    return null;
  }

  get sport(): Sport {
    if (this.tournament?.sport?.id) {
      return this.tournament.sport;
    }
    if (this.team?.sport?.id) {
      return this.team.sport;
    }
    if (this.competitorTeam?.sport?.id) {
      return this.competitorTeam.sport;
    }
  }

  @ToFrontHook
  static toFront(data: any): any {}

  @ToBackHook
  static toBack(data: any): any {}
}
